import React, { memo, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Filters, QuickFilters } from '@Sage/ssm-web5';
import { mappedActivityTypeIds } from 'models/ActivityModel';
import { CONVERSATIONS } from 'constants/Entities';
import { getCrossID, getRealID } from 'utils/filters';
import { intercomHideAndShow } from 'utils/intercom';
import { userflowHideAndShow } from 'utils/userflow';
import { logEvent } from 'utils/tracking';
import { inConversations } from 'containers/Activities/utils/conversations';
import useAdvancedFilters from './hooks/useAdvancedFilters';
import useCrossFilters from './hooks/useCrossFilters';
import useFilterState from './hooks/useFilterState';
import useFilterProcessing from './hooks/useFilterProcessing';
import useFilterValues from './hooks/useFilterValues';
import useFieldFormatting from './hooks/useFieldFormatting';
import useFilterOperations from './hooks/useFilterOperations';
import useViewsManagement from './hooks/useViewsManagement';
import useFilterProps from './hooks/useFilterProps';
import { getStateCached, getMappedSchema, getExtraFiltersSchema } from './utils/filterHelpers';
import mapStateToProps from './redux/selectors';
import mapDispatchToProps from './redux/actions';

const NewEntityFilters = memo(
    ({
        className,
        useQuickFilters,
        useQuickView,
        useViewsInFilters,
        useViewsInMenu,
        entity,
        filterKey,
        isOpen,
        defaultQuickFilters = [],
        defaultFilters = [],
        defaultCrossFilters,
        ignoreDefaultCrossFilters = false,
        hiddenFilters: hiddenFiltersProp = [],
        cache,
        customCache,
        filters,
        serverList,
        principalFilter,
        initFilters,
        beforeSchema,
        beforeExtraSchema,
        beforeCrossSchema,
        beforeCrossExtraSchema,
        changeFilter,
        customChangeFilter,
        deleteMultipleFilters,
        customDeleteMultipleFilters,
        clearFilters,
        customClearFilters,
        toggleFilters,
        filtersAddButtonSchema = [],
        saveCache,
        customSaveCache,
        getFuzzy,
        getList,
        views,
        createView,
        removeView,
        useDrawer,
        dontIgnoreTheseFilters,
        viewsPublicManagement,
        viewsPrivateManagement,
        standardFieldsSchemaMap,
        getCanClearAll,
        clearSpecificFilter,
        setConfigWeb,
        filterDragging,
        setFilterDragging,
        operators,
        changeAdvancedFilter,
        customChangeOperator,
        license,
        enableSalesOrdersTypes,
        crossFilters,
        crossOperators,
        disableForceCrossFilters,
        avoidStorage = false,
        avoidQuickFilters = false,
        showQuickFiltersAdd = false,
        useAddIcon = false,
        forceSelectAllTypes,
        entityToForceLabel,
        customGetSubFilterParentValue,
        multiEnvironmentData = {},
        ...props
    }) => {
        // Multi-environment: hide environment filter if multi-environment is active
        const hiddenFilters = useMemo(() => {
            const { isMultiEnvironmentActive, environmentFilterField } = multiEnvironmentData;
            if (isMultiEnvironmentActive && environmentFilterField) {
                return [...hiddenFiltersProp, environmentFilterField];
            }
            return hiddenFiltersProp;
        }, [hiddenFiltersProp, multiEnvironmentData]);
        /**
         * Available dispatch actions:
         *
         * - `init`: Initializes the state with provided payload data.
         * - `openFilters`: Updates `openFilters` state.
         * - `openQuickFilters`: Updates `openQuickFilters` state.
         * - `visibleFilters`: Sets visible filters.
         * - `quickFilters`: Updates the list of quick filters.
         * - `crossFilters`: Updates cross-schema mappings.
         * - `saveConfig`: Saves the entire configuration using payload data.
         *
         */
        const { state, dispatch, visibleFilters, openQuickFilters, quickFilters, openFilters } =
            useFilterState(cache, customCache, avoidStorage, useQuickFilters);

        const refs = useRef({
            isFirstLoad: true,
            debounce: null,
            cachedOptions: {},
        });

        const location = useLocation();

        const {
            crossLabels,
            getCrossFilters,
            getCrossValues,
            getDefaultFilters,
            getFinalCrossSchema,
            getHasCrossFiltersActive,
            getModalOptionsSchema,
            hasCrossFilters,
            initCrossFilters,
        } = useCrossFilters({
            entity,
            filterKey,
            defaultCrossFilters,
            ignoreDefaultCrossFilters,
            beforeCrossSchema,
            beforeCrossExtraSchema,
            multiEnvironmentData,
        });

        const {
            getAllowedOperators,
            getFieldsWithoutAdvancedFilters,
            getHasAdvancedFilters,
            onChangeOperator,
        } = useAdvancedFilters({
            entity,
            avoidStorage,
            customOperators: operators,
            customCrossOperators: crossOperators,
            customChangeOperator,
        });

        const { filtersToIgnore, mappedVisibleFilters, mappedQuickFilters, newDefaultFilters } =
            useFilterProcessing(
                entity,
                enableSalesOrdersTypes,
                dontIgnoreTheseFilters,
                visibleFilters,
                quickFilters,
                state,
                hasCrossFilters,
                getDefaultFilters,
                avoidStorage,
            );

        const { values, formatRangeValueToSave } = useFilterValues(
            filters,
            crossFilters,
            state,
            entity,
            hasCrossFilters,
            getCrossValues,
            filtersToIgnore,
        );

        const getCachedState = useCallback(() => {
            return getStateCached({
                cache,
                defaultQuickFilters,
                defaultFilters,
                entity,
            });
        }, [cache, defaultQuickFilters, defaultFilters, entity]);

        const init = useCallback(() => {
            const promises = [initFilters(entity)];
            if (hasCrossFilters) promises.push(initCrossFilters(entity));

            Promise.all(promises).then(([generalFilters, crossFilters]) => {
                const cachedState = getCachedState();

                //
                // Init general Filters
                // ------------------------------------------------------------
                const [schema, extraFieldsTab] = generalFilters;

                let newSchema = schema;

                if (beforeSchema) {
                    newSchema = beforeSchema(schema);
                }

                let newExtraFieldsTab = extraFieldsTab;

                if (beforeExtraSchema) {
                    newExtraFieldsTab = beforeExtraSchema(newExtraFieldsTab);
                }

                const { schemaByFieldId } = getMappedSchema(newSchema);

                let cachedVisible = cachedState?.visibleFilters;
                let finalFilters = cachedVisible ? [...cachedVisible] : [...defaultFilters];

                finalFilters = finalFilters.filter((current) => {
                    return !filtersToIgnore.includes(current);
                });

                // Adding extra field groups
                const groupedExtraFields = newExtraFieldsTab.reduce((groups, tab) => {
                    groups.push({
                        label: tab.title,
                        options: tab.fields.map((field) => field.id),
                    });
                    return groups;
                }, []);

                // Remove from extraSchema the filters that are only meant to be used or shown as cross-filters
                let finalExtraSchema = [
                    ...(filtersAddButtonSchema || []),
                    ...groupedExtraFields,
                ].reduce((arr, obj) => {
                    obj.options = [...obj.options].filter(
                        (option) =>
                            !schemaByFieldId[option]?.isFakeCrossFilter &&
                            !schemaByFieldId[option]?.showOnlyAsCrossFilter,
                    );
                    arr.push(obj);
                    return arr;
                }, []);

                let payload = {
                    ...cachedState,
                    schema: newSchema,
                    mappedSchema: schemaByFieldId,
                    visibleFilters: finalFilters,
                    extraSchema: finalExtraSchema,
                    updatedDefaultFilters: [...defaultFilters],
                };

                //
                // Init cross-filters (if available)
                // ------------------------------------------------------------
                if (crossFilters) {
                    const { crossSchema, visibleFilters: finalVisibleFilters } = getCrossFilters({
                        crossFilters,
                        visibleFilters: finalFilters,
                        getMappedSchema,
                        filtersToIgnore,
                    });
                    payload.crossSchema = crossSchema;
                    payload.visibleFilters = finalVisibleFilters;
                }

                dispatch({
                    type: 'init',
                    payload,
                });
            });
        }, [
            initFilters,
            entity,
            hasCrossFilters,
            initCrossFilters,
            getCachedState,
            defaultFilters,
            filtersAddButtonSchema,
            dispatch,
            filtersToIgnore,
            getCrossFilters,
            beforeSchema,
            beforeExtraSchema,
        ]);

        useEffect(() => {
            if (refs.current.isFirstLoad) {
                init();
                refs.current.isFirstLoad = false;
            }
        }, [init]);

        const extraFiltersSchema = useMemo(() => {
            return getExtraFiltersSchema({
                schema: state.schema,
                extraSchema: state.extraSchema,
                hiddenFilters,
                entity,
            });
        }, [state.schema, state.extraSchema, hiddenFilters, entity]);

        const saveNewCache = useCallback(
            (newCache, partial) => {
                if (avoidStorage) {
                    customSaveCache && customSaveCache(newCache);
                    return Promise.resolve();
                }

                const compareFields = useQuickFilters
                    ? ['openQuickFilters', 'quickFilters']
                    : ['visibleFilters', 'openFilters', 'openQuickFilters', 'quickFilters'];

                const payload = compareFields.reduce((obj, field) => {
                    obj[field] = newCache[field] || state[field];
                    return obj;
                }, {});
                return saveCache(payload, partial);
            },
            [useQuickFilters, state, saveCache, avoidStorage, customSaveCache],
        );

        const relatedEntity = useMemo(() => {
            switch (true) {
                case inConversations(location):
                    return CONVERSATIONS;
                default:
                    return null;
            }
        }, [location]);

        const handleSubfilters = useCallback(
            ({ values, oldValues, filter, crossEntity }) => {
                if (!values) return;

                const { id: realID } = getRealID(filter);
                const crossId = getCrossID(filter, crossEntity);

                switch (realID) {
                    case 'activityType':
                        const newAddedValue = [...values].pop();
                        const toggleNewSubfilter = newAddedValue && !oldValues?.length;
                        const isNewSubfilter =
                            oldValues.filter((value) => value.value === newAddedValue).length === 0;

                        if (toggleNewSubfilter || isNewSubfilter) {
                            const newAddedSubfilter = mappedActivityTypeIds[newAddedValue];
                            if (newAddedSubfilter) {
                                onToggleFilter({
                                    filter: getCrossID(newAddedSubfilter, crossEntity),
                                    hasSubfilters: false,
                                    parentFilter: crossId,
                                    overrideState: state,
                                });
                            }
                        }
                        break;
                    case 'idCampaign':
                        const newAddedSubfilter = !oldValues?.length && values?.length;
                        if (newAddedSubfilter) {
                            onToggleFilter({
                                filter: getCrossID('campaignStatus', crossEntity),
                                hasSubfilters: false,
                                parentFilter: crossId,
                                overrideState: state,
                                overrideOpen: true,
                            });
                        }
                        break;
                    default:
                        break;
                }
            },
            [onToggleFilter, state],
        );

        const loadFuzzyOptions = useCallback(
            (field) => {
                return (text) => {
                    return new Promise((resolve) => {
                        if (!field.inputAttrs) return resolve([]);

                        // Multi-environment: include environment in cache key and request
                        const environmentId = multiEnvironmentData.isMultiEnvironmentActive
                            ? multiEnvironmentData.selectedEnvironmentIds?.join(',')
                            : null;
                        const cacheEnvKey = environmentId ? `_env${environmentId}` : '';
                        const cachedOptionsKey = `${field.inputAttrs.list}__${text}${cacheEnvKey}`;

                        if (refs.current.cachedOptions[cachedOptionsKey])
                            return resolve(refs.current.cachedOptions[cachedOptionsKey]);
                        clearTimeout(refs.current.debounce);
                        refs.current.debounce = setTimeout(() => {
                            getFuzzy({
                                field: field.inputAttrs.field,
                                list: field.inputAttrs.list,
                                text,
                                feature: entity.entity,
                                defaultSearch: field.inputAttrs.defaultSearch,
                                environmentId,
                            })
                                .then((options) => {
                                    let newOptions = options;
                                    if (field.beforeRenderValueList) {
                                        newOptions = field.beforeRenderValueList(
                                            newOptions,
                                            field?.inputAttrs?.list,
                                        );
                                    }
                                    if (field.groupFunction) {
                                        newOptions = field.groupFunction(newOptions);
                                    }
                                    refs.current.cachedOptions[cachedOptionsKey] = newOptions;
                                    resolve(newOptions);
                                })
                                .catch(console.error);
                        }, 500);
                    });
                };
            },
            [entity, getFuzzy, multiEnvironmentData],
        );

        const loadOptions = useCallback(
            (field) => () => {
                if (!field?.inputAttrs?.list) return Promise.resolve([]);

                return new Promise((resolve, reject) => {
                    getList(field.inputAttrs.list)
                        .then((options) => {
                            let newOptions = options;

                            // Multi-environment: filter options by stridenvironment field
                            if (multiEnvironmentData.isMultiEnvironmentActive) {
                                const selectedEnvIds =
                                    multiEnvironmentData.selectedEnvironmentIds || [];
                                newOptions = newOptions.filter((option) => {
                                    if (!option?.stridenvironment) return true;
                                    const normalized = String(option.stridenvironment)
                                        .replace(/;/g, ' ')
                                        .trim();
                                    const envValues = normalized.split(/\s+/).filter(Boolean);
                                    if (envValues.length === 0) return true;
                                    if (envValues.includes('-1')) return true;
                                    return envValues.some((envId) =>
                                        selectedEnvIds.includes(envId),
                                    );
                                });
                            }

                            if (field.beforeRenderValueList) {
                                newOptions = field.beforeRenderValueList(
                                    newOptions,
                                    field?.inputAttrs?.list,
                                );
                            }
                            if (field.groupFunction) {
                                newOptions = field.groupFunction(newOptions);
                            }
                            resolve(newOptions);
                        })
                        .catch(reject);
                });
            },
            [getList, multiEnvironmentData],
        );

        // Field formatting logic
        const { getFieldsFormatted } = useFieldFormatting(
            standardFieldsSchemaMap,
            principalFilter,
            loadFuzzyOptions,
            loadOptions,
            filters,
            operators,
        );

        // Filter operations (handlers)
        const { onChangeFilter, onDeleteFilter, onDeleteMultipleFilters, onClearFilters } =
            useFilterOperations({
                entity,
                avoidStorage,
                useQuickFilters,
                operators,
                crossOperators,
                getHasAdvancedFilters,
                formatRangeValueToSave,
                handleSubfilters,
                values,
                changeFilter,
                customChangeFilter,
                relatedEntity,
                dispatch,
                saveNewCache,
                quickFilters,
                visibleFilters,
                openFilters,
                openQuickFilters,
                crossFilters,
                filters,
                changeAdvancedFilter,
                customChangeOperator,
                disableForceCrossFilters,
                deleteMultipleFilters,
                customDeleteMultipleFilters,
                clearFilters,
                customClearFilters,
                clearSpecificFilter,
            });

        // Views management
        const {
            onChangeView,
            onChangeCustomView,
            onDeleteCustomView,
            onSaveView,
            onDeleteView,
            formatViews,
            formatCustomView,
        } = useViewsManagement({
            createView,
            removeView,
            clearFilters,
            customClearFilters,
            onChangeFilter,
            onDeleteFilter,
            entity,
            avoidStorage,
            filters,
        });

        const onResetVisibles = useCallback(() => {
            if (!avoidStorage) {
                logEvent({
                    event: entity.trueName,
                    functionality: 'resetFilterSelection',
                });
            }
        }, [entity, avoidStorage]);

        const onAddFilter = useCallback(
            (filters) => {
                const newVisibleFilters = [...filters];
                const newQuickFilters = quickFilters.filter((filter) => filters.includes(filter));

                if (!avoidStorage) {
                    dispatch({ type: 'visibleFilters', payload: newVisibleFilters });
                    dispatch({ type: 'quickFilters', payload: newQuickFilters });
                }

                saveNewCache({
                    visibleFilters: newVisibleFilters,
                    quickFilters: newQuickFilters,
                });
            },
            [dispatch, saveNewCache, quickFilters, avoidStorage],
        );

        const onAddQuickFilter = useCallback(
            (filters) => {
                const newVisibleFilters = [...filters];
                const newQuickFilters = [...filters];

                if (!avoidStorage) {
                    dispatch({ type: 'quickFilters', payload: newQuickFilters });
                    dispatch({ type: 'visibleFilters', payload: newVisibleFilters });
                }

                saveNewCache({
                    visibleFilters: newVisibleFilters,
                    quickFilters: newQuickFilters,
                });
            },
            [dispatch, saveNewCache, avoidStorage],
        );

        const onToggleFilter = useCallback(
            ({
                filter,
                hasSubfilters = false,
                parentFilter = null,
                overrideState,
                overrideOpen = false,
            }) => {
                const type = !!useQuickFilters ? 'openQuickFilters' : 'openFilters';
                const finalState = overrideState || state;
                const openFilters = finalState[type] || {};
                let newOpenFilters = {};

                // Helper function to set the subfilters values
                const toggleSubfilters = (subfilters = {}, filter = null) => {
                    const newSubfilters = Object.entries(subfilters).reduce((obj, [key, value]) => {
                        obj[key] = key === filter ? !value : false; // Toggle the specified subfilter and close others
                        return obj;
                    }, {});
                    if (filter && !newSubfilters.hasOwnProperty(filter)) {
                        newSubfilters[filter] = true;
                    }
                    return newSubfilters;
                };

                if (!parentFilter) {
                    // Toggle main level filters
                    newOpenFilters = Object.entries(openFilters).reduce((obj, [key, value]) => {
                        let newValue;

                        switch (true) {
                            case key === filter && hasSubfilters:
                                newValue = { ...value, open: overrideOpen || !value.open };
                                break;
                            case key === filter && !hasSubfilters:
                                newValue = overrideOpen || !value;
                                break;
                            default:
                                newValue =
                                    typeof value === 'boolean'
                                        ? overrideOpen || false
                                        : { ...value, open: overrideOpen || false };
                                break;
                        }

                        obj[key] = newValue;

                        return obj;
                    }, {});
                    if (!newOpenFilters.hasOwnProperty(filter)) {
                        newOpenFilters[filter] = hasSubfilters
                            ? { open: true, subfilters: {} }
                            : true;
                    }
                } else {
                    // Toggle subfilters
                    newOpenFilters = Object.entries(openFilters).reduce((obj, [key, value]) => {
                        if (key === parentFilter) {
                            obj[key] = {
                                open: true,
                                subfilters: toggleSubfilters(value.subfilters, filter),
                            };
                        } else {
                            obj[key] =
                                typeof value === 'boolean' ? false : { ...value, open: false };
                        }
                        return obj;
                    }, {});
                    if (!newOpenFilters.hasOwnProperty(parentFilter)) {
                        newOpenFilters[parentFilter] = {
                            open: true,
                            subfilters: toggleSubfilters({}, filter),
                        };
                    }
                }

                saveNewCache(
                    {
                        [type]: newOpenFilters,
                    },
                    !!useQuickFilters,
                );
            },
            [useQuickFilters, state, saveNewCache],
        );

        const formattedViews = useMemo(
            () => formatViews(views, entity),
            [views, entity, formatViews],
        );

        const toggleQuickFilter = useCallback(
            (filter) => {
                const alreadyExists = quickFilters.includes(filter);
                const newQuickFilters = alreadyExists
                    ? quickFilters.filter((i) => i !== filter)
                    : [...quickFilters, filter];
                dispatch({ type: 'quickFilters', payload: newQuickFilters });
                saveNewCache({
                    quickFilters: newQuickFilters,
                });

                logEvent({
                    event: entity.trueName,
                    functionality: alreadyExists ? 'unpinQuickFilter' : 'pinQuickFilter',
                });
            },
            [dispatch, entity.trueName, saveNewCache, quickFilters],
        );

        const onReOrder = useCallback(
            (startIndex, endIndex) => {
                const result = [...quickFilters];
                const [removed] = result.splice(startIndex, 1);
                result.splice(endIndex, 0, removed);
                dispatch({ type: 'quickFilters', payload: result });
                saveNewCache({
                    quickFilters: result,
                });

                logEvent({
                    event: entity.trueName,
                    functionality: 'orderQuickFilter',
                });
            },
            [dispatch, entity.trueName, saveNewCache, quickFilters],
        );

        const onClose = useCallback(() => {
            toggleFilters(entity, false);
            intercomHideAndShow();
            userflowHideAndShow('show');
        }, [entity, toggleFilters]);

        const onDrag = useCallback(
            (item) => {
                setFilterDragging(entity, item.draggableId);
            },
            [entity, setFilterDragging],
        );

        const onDrop = useCallback(
            (item) => {
                // Keep state for eye tracking (ux)
                setTimeout(() => {
                    setFilterDragging(entity, null);
                }, 750);
            },
            [entity, setFilterDragging],
        );

        const getSubfilterSchema = useCallback(
            ({ crossEntity, filter, key, schema }) => {
                // Early return if no schema
                if (!schema || !filter || !key) return [];

                // Get appropriate filters based on whether it's a cross filter
                const entityFilters = crossEntity ? crossFilters?.[crossEntity] : filters;
                let parentFilterValue = entityFilters?.[filter]?.value;

                // In ForceBI for example uses only completeValues, so the value comes inside completeValues[0].value and not in value
                if (customGetSubFilterParentValue) {
                    parentFilterValue = customGetSubFilterParentValue(entityFilters?.[filter]);
                }

                // Early return if no parent filter value
                if (!parentFilterValue?.length) return [];

                // Default condition for showing subfilters
                const defaultCondition = (values, check) => values.includes(check);

                return Object.values(schema).reduce((subfilters, item) => {
                    const {
                        description,
                        id,
                        isSubFilter,
                        filterTooltip,
                        [key]: parentFilterId,
                        customShowSubfilterCondition,
                    } = item;

                    // Skip if not a valid subfilter
                    if (!isSubFilter || !parentFilterId) return subfilters;

                    // Use custom or default condition to check if subfilter should be shown
                    const showCondition = customShowSubfilterCondition || defaultCondition;
                    if (!showCondition(parentFilterValue, parentFilterId)) return subfilters;

                    // Create base subfilter object
                    const subfilter = {
                        fields: [...getFieldsFormatted(item)],
                        id,
                        filterTooltip,
                        label: description,
                        isPinnable: false,
                        isRemovable: false,
                    };

                    // Add cross filter specific properties if needed
                    if (crossEntity) {
                        subfilter.entity = crossEntity;
                        subfilter.crossId = getCrossID(id, crossEntity);
                    }

                    subfilters.push(subfilter);
                    return subfilters;
                }, []);
            },
            [crossFilters, filters, getFieldsFormatted, customGetSubFilterParentValue],
        );

        const useViews = useMemo(() => {
            if (!useViewsInFilters && !useViewsInMenu) return false;
            return true;
        }, [useViewsInFilters, useViewsInMenu]);

        const hasFiltersActive = useMemo(() => {
            if (!filters) return false;
            let { entityShowSelected, matchingName, idProductFamily, ...otherFilters } = filters;
            if (enableSalesOrdersTypes) {
                // Removing salesOrderType
                otherFilters = Object.keys(otherFilters).reduce((obj, filterKey) => {
                    if (filterKey === 'salesOrderType') return obj;
                    obj[filterKey] = otherFilters[filterKey];
                    return obj;
                }, {});
            }
            return (
                Object.entries(otherFilters).length > 0 || getHasCrossFiltersActive(crossFilters)
            );
        }, [filters, crossFilters, enableSalesOrdersTypes, getHasCrossFiltersActive]);

        const handleChangeOperator = useCallback(
            ({ filter, value, crossEntity }) => {
                const finalCrossEntity = !filter.isCrossFilter ? crossEntity : null;
                onChangeOperator({
                    filter,
                    value,
                    values,
                    useQuickFilters,
                    crossEntity: finalCrossEntity,
                });
            },
            [onChangeOperator, useQuickFilters, values],
        );

        const { commonProps, quickFiltersBaseProps, quickFiltersAddProps, standardFiltersProps } =
            useFilterProps({
                // Common props
                entity,
                values,
                operators,
                crossOperators,
                formatCustomView,
                formatViews: formattedViews,
                getAllowedOperators,
                getFieldsWithoutAdvancedFilters,
                getHasAdvancedFilters,
                onChangeFilter,
                onChangeCustomView,
                onChangeView,
                onClearFilters,
                handleChangeOperator,
                forceSelectAllTypes,

                // Quick filters specific
                className,
                crossLabels,
                filterDragging,
                quickFilters,
                getCanClearAll,
                hasFiltersActive,
                license,
                onToggleFilter,
                init,
                openQuickFilters,
                useQuickView,
                entityToForceLabel,

                // Quick filters add props
                showQuickFiltersAdd,
                useAddIcon,
                newDefaultFilters,
                extraFiltersSchema,
                onAddQuickFilter,
                onDeleteMultipleFilters,
                onResetVisibles,
                visibleFilters,
                hasCrossFilters,
                getModalOptionsSchema,
                state,
                getExtraFiltersSchema,

                // Standard filters specific
                viewsPrivateManagement,
                viewsPublicManagement,
                isOpen,
                onClose,
                onAddFilter,
                onDeleteFilter,
                onDeleteCustomView,
                onDeleteView,
                onDrag,
                onDrop,
                onReOrder,
                onSaveView,
                toggleQuickFilter,
                avoidQuickFilters,
                useDrawer,
                useViewsInFilters,
                useViewsInMenu,
                openFilters,
                filtersToIgnore,
                useViews,

                // Schema formatting dependencies
                mappedSchema: state.mappedSchema,
                hiddenFilters,
                getFieldsFormatted,
                getSubfilterSchema,
                crossSchema: state.crossSchema,
                getFinalCrossSchema,
                mappedQuickFilters,
                mappedVisibleFilters,
            });

        const filtersProps = useMemo(() => {
            if (useQuickFilters) {
                return {
                    ...quickFiltersBaseProps,
                    ...quickFiltersAddProps,
                    ...commonProps,
                };
            }

            return {
                ...standardFiltersProps,
                ...commonProps,
                ...props,
            };
        }, [
            useQuickFilters,
            quickFiltersBaseProps,
            quickFiltersAddProps,
            commonProps,
            standardFiltersProps,
            props,
        ]);

        // Key to force remount when selected environments change
        const environmentKey = useMemo(() => {
            if (!multiEnvironmentData.isMultiEnvironmentActive) return '';
            return `_env_${multiEnvironmentData.selectedEnvironmentIds?.join('-') || ''}`;
        }, [multiEnvironmentData]);

        const FiltersComponent = useMemo(() => {
            if (useQuickFilters) {
                return <QuickFilters key={`quick${environmentKey}`} {...filtersProps} />;
            }
            return <Filters key={`filters${environmentKey}`} {...filtersProps} />;
        }, [useQuickFilters, filtersProps, environmentKey]);

        return FiltersComponent;
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(NewEntityFilters);
